import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PaymentsIcon from "@mui/icons-material/Payments";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StorageIcon from "@mui/icons-material/Storage";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import {
  EventInvitation,
  EventJoinRequest,
  EventSummary,
} from "src/app/models/screens/events.model";
import { FilesStorageRequest } from "src/app/models/screens/files.model";
import {
  Payment,
  UpcomingPayment,
} from "src/app/models/screens/payments.model";
import { Task, TaskRequest } from "src/app/models/screens/task.model";
import PodDashboardScreenViewModel, {
  PodDashboardLogEntry,
} from "src/app/screens/podDashboard/podDashboard.viewmodel";
import styles from "src/app/styles/screens/podDashboard.style";

const PodDashboardScreenView: React.FC = () => {
  const {
    role,
    selectorUsers,
    isLoadingChildren,
    selectedDashboardOwnerId,
    onSelectedDashboardOwnerChanged,

    todaysTasks,
    isLoadingTodaysTasks,
    highlightedTaskId,

    pendingTaskRequests,
    isLoadingTaskRequests,
    onAcceptTaskRequest,
    onRejectTaskRequest,
    onAcceptMoreTimeRequest,
    onRejectMoreTimeRequest,

    upcomingEvents,
    isLoadingUpcomingEvents,

    logs,
    isLoadingLogs,

    recentPayments,
    isLoadingRecentPayments,

    pendingJoinRequests,
    isLoadingJoinRequests,
    respondingJoinRequestId,
    onRespondJoinRequest,

    pendingInvitations,
    isLoadingInvitations,
    respondingInvitationId,
    onRespondInvitation,

    companionPrompt,
    companionRelatives,
    isLoadingCompanionRelatives,
    selectedCompanionIds,
    isSubmittingCompanionPrompt,
    toggleCompanionSelection,
    submitCompanionPrompt,
    closeCompanionPrompt,

    scheduledPayments,
    isLoadingScheduledPayments,

    pendingStorageRequests,
    isLoadingStorageRequests,
    decidingStorageRequestId,
    onDecideStorageRequest,

    minutesToLabel,
    formatCurrency,
    formatDateTime,
    getTaskStatusChipColor,
    getRequestStatusChipColor,
    getStorageRequestStatusChipColor,
    getLogTypeChipColor,

    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
  } = PodDashboardScreenViewModel();

  const renderTodaysTasksSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <AssignmentIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>Today's Tasks</Typography>
      </Box>

      {isLoadingTodaysTasks ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : todaysTasks.length === 0 ? (
        <Typography sx={styles.emptyStateText}>
          No tasks scheduled for today.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {todaysTasks.map((task: Task) => {
            const isHighlighted = highlightedTaskId === task.id;
            return (
              <Grid key={task.id} size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={[
                    styles.innerCard,
                    isHighlighted ? styles.highlightedInnerCard : {},
                    task.isPriority ? styles.priorityInnerCard : {},
                  ]}
                >
                  <Box sx={styles.itemHeaderRow}>
                    <Typography sx={styles.itemTitleText}>
                      {task.title}
                    </Typography>
                    {task.tag && (
                      <Chip
                        size="small"
                        label={task.tag}
                        sx={[
                          styles.tagChip,
                          { backgroundColor: task.tagColor },
                        ]}
                      />
                    )}
                    <Chip
                      size="small"
                      label={task.status}
                      color={getTaskStatusChipColor(task.status)}
                    />
                  </Box>

                  <Typography sx={styles.itemMetaText}>
                    Time: {minutesToLabel(task.from)} -{" "}
                    {minutesToLabel(task.to)}
                  </Typography>

                  {task.isAssigned && task.assignedByFullName && (
                    <Typography sx={styles.itemMetaText}>
                      Assigned by: {task.assignedByFullName}
                    </Typography>
                  )}

                  {task.description && (
                    <Typography sx={styles.itemDescriptionText}>
                      {task.description}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );

  const renderPendingTaskRequestsSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <AssignmentLateIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>Pending Task Requests</Typography>
      </Box>

      {isLoadingTaskRequests ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : pendingTaskRequests.length === 0 ? (
        <Typography sx={styles.emptyStateText}>
          No pending task requests.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {pendingTaskRequests.map((request: TaskRequest) => (
            <Grid key={request.id} size={{ xs: 12 }}>
              <Paper
                elevation={0}
                sx={[
                  styles.innerCard,
                  request.taskIsPriority ? styles.priorityInnerCard : {},
                ]}
              >
                <Box sx={styles.itemHeaderRow}>
                  <Typography sx={styles.itemTitleText}>
                    {request.taskTitle}
                  </Typography>
                  {request.taskTag && (
                    <Chip
                      size="small"
                      label={request.taskTag}
                      sx={[
                        styles.tagChip,
                        { backgroundColor: request.taskTagColor },
                      ]}
                    />
                  )}
                  <Chip
                    size="small"
                    label={request.status}
                    color={getRequestStatusChipColor(request.status)}
                  />
                </Box>

                <Typography sx={styles.itemMetaText}>
                  From: {request.requesterFullName}
                </Typography>
                <Typography sx={styles.itemMetaText}>
                  Date: {request.taskDate}
                </Typography>
                <Typography sx={styles.itemMetaText}>
                  Time: {minutesToLabel(request.taskFrom)} -{" "}
                  {minutesToLabel(request.taskTo)}
                </Typography>

                {request.taskDescription && (
                  <Typography sx={styles.itemDescriptionText}>
                    {request.taskDescription}
                  </Typography>
                )}

                {request.moreTimeRequested &&
                  request.moreTimeStatus === "Pending" && (
                    <>
                      <Typography sx={styles.itemMetaText}>
                        Reschedule requested to {request.moreTimeRequestedDate}{" "}
                        {minutesToLabel(request.moreTimeRequestedFrom)} -{" "}
                        {minutesToLabel(request.moreTimeRequestedTo)}
                      </Typography>
                      <Box sx={styles.itemActionsContainer}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CheckCircleOutlineIcon />}
                          onClick={() => onAcceptMoreTimeRequest(request.id)}
                        >
                          Accept Reschedule
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<CloseIcon />}
                          onClick={() => onRejectMoreTimeRequest(request.id)}
                        >
                          Reject Reschedule
                        </Button>
                      </Box>
                    </>
                  )}

                {!request.moreTimeRequested && (
                  <Box sx={styles.itemActionsContainer}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => onAcceptTaskRequest(request.id)}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CloseIcon />}
                      onClick={() => onRejectTaskRequest(request.id)}
                    >
                      Reject
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );

  const renderUpcomingEventsSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <EventIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>
          Next 7 Days — My Events
        </Typography>
      </Box>

      {isLoadingUpcomingEvents ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : upcomingEvents.length === 0 ? (
        <Typography sx={styles.emptyStateText}>
          No events in the next 7 days.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {upcomingEvents.map((evt: EventSummary) => (
            <Grid key={evt.id} size={{ xs: 12 }}>
              <Paper elevation={0} sx={styles.innerCard}>
                <Box sx={styles.itemHeaderRow}>
                  <Typography sx={styles.itemTitleText}>{evt.title}</Typography>
                  {evt.isPublic && (
                    <Chip size="small" label="Public" color="primary" />
                  )}
                  {evt.isPaid && (
                    <Chip
                      size="small"
                      label={formatCurrency(evt.participationFee, evt.currency)}
                      color="secondary"
                    />
                  )}
                </Box>

                <Typography sx={styles.itemMetaText}>
                  Date: {evt.date}
                </Typography>
                <Typography sx={styles.itemMetaText}>
                  Time: {minutesToLabel(evt.from)} - {minutesToLabel(evt.to)}
                </Typography>
                <Typography sx={styles.itemMetaText}>
                  Location: {evt.location}
                </Typography>
                <Typography sx={styles.itemMetaText}>
                  Hosted by: {evt.creatorFullName}
                </Typography>

                {evt.description && (
                  <Typography sx={styles.itemDescriptionText}>
                    {evt.description}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );

  const renderLogsSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <DescriptionIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>Recent Logs</Typography>
      </Box>

      {isLoadingLogs ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : logs.length === 0 ? (
        <Typography sx={styles.emptyStateText}>No logs found.</Typography>
      ) : (
        <Box sx={styles.logsListContainer}>
          {logs.map((log: PodDashboardLogEntry) => (
            <Paper key={log.id} elevation={0} sx={styles.innerCard}>
              <Box sx={styles.logHeaderRow}>
                <Typography sx={styles.logIdText}>{log.id}</Typography>
                <Chip
                  size="small"
                  label={log.type}
                  color={getLogTypeChipColor(log.type)}
                />
              </Box>
              <Typography sx={styles.logCreatedAtText}>
                {formatDateTime(log.createdAt)}
              </Typography>
              <Typography sx={styles.logText}>{log.text}</Typography>
            </Paper>
          ))}
        </Box>
      )}
    </Paper>
  );

  const renderRecentPaymentsSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <PaymentsIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>
          Last 7 Days — Received Payments
        </Typography>
      </Box>

      {isLoadingRecentPayments ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : recentPayments.length === 0 ? (
        <Typography sx={styles.emptyStateText}>
          No payments received in the last 7 days.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {recentPayments.map((payment: Payment) => (
            <Grid key={payment.id} size={{ xs: 12 }}>
              <Paper elevation={0} sx={styles.innerCard}>
                <Typography sx={styles.paymentAmountText}>
                  {formatCurrency(payment.amount, payment.currency)}
                </Typography>
                <Box sx={styles.paymentMetaRow}>
                  <Typography sx={styles.paymentMetaText}>
                    From: {payment.senderFullName}
                  </Typography>
                  <Typography sx={styles.paymentMetaText}>
                    {payment.date}
                  </Typography>
                </Box>
                {payment.description && (
                  <Typography sx={styles.itemDescriptionText}>
                    {payment.description}
                  </Typography>
                )}
                <Chip
                  size="small"
                  label={payment.status}
                  color={
                    payment.status === "Completed"
                      ? "success"
                      : payment.status === "Pending"
                        ? "warning"
                        : "error"
                  }
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );

  const renderPendingJoinRequestsSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <GroupAddIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>
          Pending Join Requests (My Public Events)
        </Typography>
      </Box>

      {isLoadingJoinRequests ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : pendingJoinRequests.length === 0 ? (
        <Typography sx={styles.emptyStateText}>
          No pending join requests.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {pendingJoinRequests.map((req: EventJoinRequest) => {
            const isResponding = respondingJoinRequestId === req.id;
            return (
              <Grid key={req.id} size={{ xs: 12 }}>
                <Paper elevation={0} sx={styles.innerCard}>
                  <Box sx={styles.itemHeaderRow}>
                    <Typography sx={styles.itemTitleText}>
                      {req.requesterFullName}
                    </Typography>
                    <Chip size="small" label={req.status} color="warning" />
                  </Box>
                  <Typography sx={styles.itemMetaText}>
                    Event: {req.event.title}
                  </Typography>
                  <Typography sx={styles.itemMetaText}>
                    Date: {req.event.date} ({minutesToLabel(req.event.from)} -{" "}
                    {minutesToLabel(req.event.to)})
                  </Typography>
                  {req.companions && req.companions.length > 0 && (
                    <Typography sx={styles.itemMetaText}>
                      {req.requesterRole === "Model"
                        ? "Coming with parents: "
                        : req.requesterRole === "Parent"
                          ? "Bringing children: "
                          : "Coming with: "}
                      {req.companions.map((c) => c.fullName).join(", ")}
                    </Typography>
                  )}
                  <Box sx={styles.itemActionsContainer}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => onRespondJoinRequest(req.id, true)}
                      disabled={isResponding}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CloseIcon />}
                      onClick={() => onRespondJoinRequest(req.id, false)}
                      disabled={isResponding}
                    >
                      Reject
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );

  const renderPendingInvitationsSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <MailOutlineIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>
          Pending Event Invitations
        </Typography>
      </Box>

      {isLoadingInvitations ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : pendingInvitations.length === 0 ? (
        <Typography sx={styles.emptyStateText}>
          No pending invitations.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {pendingInvitations.map((inv: EventInvitation) => {
            const isResponding = respondingInvitationId === inv.id;
            return (
              <Grid key={inv.id} size={{ xs: 12 }}>
                <Paper elevation={0} sx={styles.innerCard}>
                  <Box sx={styles.itemHeaderRow}>
                    <Typography sx={styles.itemTitleText}>
                      {inv.event.title}
                    </Typography>
                    <Chip size="small" label={inv.status} color="warning" />
                  </Box>
                  <Typography sx={styles.itemMetaText}>
                    Invited by: {inv.senderFullName}
                  </Typography>
                  <Typography sx={styles.itemMetaText}>
                    Date: {inv.event.date} ({minutesToLabel(inv.event.from)} -{" "}
                    {minutesToLabel(inv.event.to)})
                  </Typography>
                  <Typography sx={styles.itemMetaText}>
                    Location: {inv.event.location}
                  </Typography>
                  {inv.event.isPaid && (
                    <Typography sx={styles.itemMetaText}>
                      Fee:{" "}
                      {formatCurrency(
                        inv.event.participationFee,
                        inv.event.currency,
                      )}
                    </Typography>
                  )}
                  <Box sx={styles.itemActionsContainer}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => onRespondInvitation(inv.id, true)}
                      disabled={isResponding}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CloseIcon />}
                      onClick={() => onRespondInvitation(inv.id, false)}
                      disabled={isResponding}
                    >
                      Reject
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );

  const renderScheduledPaymentsSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <ScheduleIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>
          Next 7 Days — Scheduled Payments
        </Typography>
      </Box>

      {isLoadingScheduledPayments ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : scheduledPayments.length === 0 ? (
        <Typography sx={styles.emptyStateText}>
          No payments scheduled in the next 7 days.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {scheduledPayments.map((payment: UpcomingPayment) => (
            <Grid key={payment.id} size={{ xs: 12 }}>
              <Paper elevation={0} sx={styles.innerCard}>
                <Box sx={styles.itemHeaderRow}>
                  <Typography sx={styles.paymentAmountText}>
                    {formatCurrency(payment.amount, payment.currency)}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      payment.daysUntilDue === 0
                        ? "Today"
                        : payment.daysUntilDue === 1
                          ? "Tomorrow"
                          : `In ${payment.daysUntilDue} days`
                    }
                    color={payment.daysUntilDue <= 1 ? "warning" : "default"}
                    sx={styles.scheduledDueChip}
                  />
                </Box>
                <Typography sx={styles.itemMetaText}>
                  To: {payment.recipientFullName}
                </Typography>
                <Typography sx={styles.itemMetaText}>
                  Date: {payment.date}
                </Typography>
                {payment.description && (
                  <Typography sx={styles.itemDescriptionText}>
                    {payment.description}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );

  const renderStorageRequestsSection = () => (
    <Paper elevation={0} sx={styles.sectionCard}>
      <Box sx={styles.sectionHeader}>
        <StorageIcon sx={styles.sectionIcon} />
        <Typography sx={styles.sectionTitle}>
          Pending Storage Requests
        </Typography>
      </Box>

      {isLoadingStorageRequests ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress size={28} />
        </Box>
      ) : pendingStorageRequests.length === 0 ? (
        <Typography sx={styles.emptyStateText}>
          No pending storage requests.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {pendingStorageRequests.map((req: FilesStorageRequest) => {
            const isDeciding = decidingStorageRequestId === req.id;
            return (
              <Grid key={req.id} size={{ xs: 12 }}>
                <Paper elevation={0} sx={styles.innerCard}>
                  <Box sx={styles.itemHeaderRow}>
                    <Typography sx={styles.itemTitleText}>
                      {req.requesterFullName}
                    </Typography>
                    <Chip
                      size="small"
                      label={req.status}
                      color={getStorageRequestStatusChipColor(req.status)}
                    />
                  </Box>
                  <Typography sx={styles.itemMetaText}>
                    Requested: <strong>{req.requestedGb} GB</strong>
                  </Typography>
                  <Typography sx={styles.logCreatedAtText}>
                    {formatDateTime(req.createdAt)}
                  </Typography>
                  <Box sx={styles.itemActionsContainer}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => onDecideStorageRequest(req.id, true)}
                      disabled={isDeciding}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CloseIcon />}
                      onClick={() => onDecideStorageRequest(req.id, false)}
                      disabled={isDeciding}
                    >
                      Reject
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="P.O.D Dashboard" />

      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              P.O.D Dashboard
            </Typography>
          </Grid>

          {role === "Parent" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="pod-dashboard-owner-select-label">
                      Dashboard Owner
                    </InputLabel>
                    <Select
                      labelId="pod-dashboard-owner-select-label"
                      value={selectedDashboardOwnerId}
                      label="Dashboard Owner"
                      onChange={(event: SelectChangeEvent) =>
                        onSelectedDashboardOwnerChanged(event.target.value)
                      }
                      disabled={isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {selectorUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 6 }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>{renderTodaysTasksSection()}</Grid>

                  {role !== "Guest" && (
                    <Grid size={{ xs: 12 }}>
                      {renderPendingTaskRequestsSection()}
                    </Grid>
                  )}

                  <Grid size={{ xs: 12 }}>{renderUpcomingEventsSection()}</Grid>

                  {role !== "Guest" && (
                    <Grid size={{ xs: 12 }}>
                      {renderPendingInvitationsSection()}
                    </Grid>
                  )}

                  {role === "Admin" && (
                    <Grid size={{ xs: 12 }}>
                      {renderStorageRequestsSection()}
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Grid container spacing={3}>
                  {role !== "Guest" && (
                    <Grid size={{ xs: 12 }}>
                      {renderRecentPaymentsSection()}
                    </Grid>
                  )}

                  {role !== "Guest" && (
                    <Grid size={{ xs: 12 }}>
                      {renderPendingJoinRequestsSection()}
                    </Grid>
                  )}

                  {role === "Admin" && (
                    <Grid size={{ xs: 12 }}>
                      {renderScheduledPaymentsSection()}
                    </Grid>
                  )}

                  <Grid size={{ xs: 12 }}>{renderLogsSection()}</Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>

      <Dialog
        open={!!companionPrompt}
        onClose={closeCompanionPrompt}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Accept Invitation</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            You're accepting "{companionPrompt?.eventTitle}".
          </Typography>
          <Typography sx={{ mb: 2, opacity: 0.75, fontSize: 13 }}>
            {companionPrompt?.relativeLabel}
            {companionPrompt?.requireAtLeastOne
              ? " — at least one must be selected."
              : " — selection is optional."}
          </Typography>
          {isLoadingCompanionRelatives ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : companionRelatives.length === 0 ? (
            <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
              {companionPrompt?.requireAtLeastOne
                ? "No connected accounts found. You won't be able to proceed without one."
                : "No connected accounts found."}
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {companionRelatives.map((rel) => {
                const checked = selectedCompanionIds.includes(rel.id);
                return (
                  <ListItemButton
                    key={rel.id}
                    onClick={() => toggleCompanionSelection(rel.id)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${rel.firstName} ${rel.lastName}`}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeCompanionPrompt}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={submitCompanionPrompt}
            variant="contained"
            loading={isSubmittingCompanionPrompt}
            disabled={
              companionPrompt?.requireAtLeastOne &&
              selectedCompanionIds.length === 0
            }
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default PodDashboardScreenView;
